<script lang="ts">
  import { wbsLabel } from "../lib/wbs";
  import {
    tasks, det, wbsNodes, statusRows, allowWeekendWork, projectStartDate, floorDay,
    selectedDay, toggleTaskDayDone, setTaskBlocked, setTaskBlockedReason,
    cycleStatusDay, openEditModal, canEdit, subcontractorColors, useSwimLanes, twoShifts, projectPhases, boardSortKey,
  } from "../lib/stores";
  import { phaseColor } from "../lib/phases";
  import { SHIFT_LABEL } from "../lib/shifts";
  import type { Shift } from "../lib/shifts";
  import { subcontractorColor } from "../lib/subcontractors";
  import { allTaskDays, boardDayCount, compareCards, groupCardsByPhase, requiredRowValue, resourcesForCards, statusConflicts, statusLabel, statusTone, tasksOnDayRequiringRow } from "../lib/board";
  import type { PhaseGroup } from "../lib/board";
  import { formatProjectDay, isWeekendDay, projectStartDay, weekdayNameOfProjectDay, weekOfProjectDay } from "../lib/projectDate";
  import type { Task } from "../lib/types";

  $: editable = $canEdit;

  // The day the clock says we're on. Null with no calendar set, in which case the
  // view simply starts at the beginning of the plan and is stepped through by hand.
  $: clockDay = $floorDay;
  // A null selection follows the clock, so a screen left running overnight rolls over
  // on its own instead of showing yesterday to the morning standup.
  $: day = $selectedDay ?? clockDay ?? 0;

  $: result = $det;
  $: es = result.ok ? result.ES : {};
  $: cards = result.ok ? allTaskDays($tasks, result, $allowWeekendWork, $twoShifts) : [];
  $: lastDay = result.ok ? boardDayCount(cards, result.projectEnd) - 1 : 0;
  $: dayCards = cards.filter((c) => c.day === day).sort((a, b) => compareCards(a, b, $boardSortKey, es, $wbsNodes));

  /** Split into shift buckets first (only when the project uses shifts), phase
   *  swimlanes within each — same two-level layout BoardTable uses for the full board. */
  interface ShiftBucket { shift: Shift | null; groups: PhaseGroup[] }
  $: shiftBuckets = ($twoShifts ? ([1, 2] as const) : ([null] as const)).map(
    (s): ShiftBucket => ({
      shift: s,
      groups: groupCardsByPhase(s ? dayCards.filter((c) => c.shift === s) : dayCards, $useSwimLanes, $projectPhases),
    })
  );

  $: resources = resourcesForCards(dayCards);
  $: doneCount = dayCards.filter((c) => c.done).length;
  $: blockedCount = dayCards.filter((c) => c.task.blocked).length;
  $: openCount = dayCards.length - doneCount;
  $: startingCount = dayCards.filter((c) => c.index === 1).length;

  $: calendarLabel = formatProjectDay($projectStartDate, day);
  $: isToday = clockDay != null && day === clockDay;
  $: isProjectStart = day === projectStartDay($projectStartDate);

  // On a site screen the surrounding page chrome — title, method note, tab bar — is
  // a third of the display saying nothing the standup needs, so the day can take over
  // the whole screen.
  let shell: HTMLElement;
  let fullscreen = false;
  function toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen();
    else shell?.requestFullscreen?.();
  }
  function onFullscreenChange() {
    fullscreen = !!document.fullscreenElement;
  }

  function step(delta: number) {
    const next = Math.max(0, Math.min(lastDay, day + delta));
    selectedDay.set(next);
  }
  function backToToday() {
    selectedDay.set(null);
  }

  /** Predecessors are the standup's "what are we waiting on" — worth showing only on a
   *  task's first day, and only with whether they've actually finished. */
  function predecessorsOf(t: Task): { id: string; name: string; wbs: string; complete: boolean }[] {
    if (!result.ok) return [];
    return t.preds.map((id) => {
      const p = $tasks.find((x) => x.id === id);
      return { id, wbs: p ? wbsLabel(p, $wbsNodes) : "WBS to assign", name: p?.name ?? "Missing predecessor", complete: !!result.ok && !!result.complete[id] };
    });
  }

  let editingReasonFor: string | null = null;
  let reasonDraft = "";
  function startBlocking(t: Task) {
    if (t.blocked) {
      setTaskBlocked(t.id, false);
      if (editingReasonFor === t.id) editingReasonFor = null;
      return;
    }
    editingReasonFor = t.id;
    reasonDraft = t.blockedReason ?? "";
  }
  function confirmBlock(t: Task) {
    setTaskBlocked(t.id, true, reasonDraft.trim());
    editingReasonFor = null;
  }
  function editReason(t: Task) {
    editingReasonFor = t.id;
    reasonDraft = t.blockedReason ?? "";
  }
  function saveReason(t: Task) {
    setTaskBlockedReason(t.id, reasonDraft.trim());
    editingReasonFor = null;
  }
  function onReasonKey(e: KeyboardEvent, t: Task, commit: (t: Task) => void) {
    if (e.key === "Enter") { e.preventDefault(); commit(t); }
    if (e.key === "Escape") { e.preventDefault(); editingReasonFor = null; }
  }
</script>

<svelte:document on:fullscreenchange={onFullscreenChange} />

<div class="today" class:fullscreen bind:this={shell}>
  {#if !result.ok}
    <div class="today-empty">Fix the schedule error above to see the day.</div>
  {:else}
    <header class="today-head">
      <div class="today-date">
        <div class="today-title">
          {#if calendarLabel}
            {calendarLabel}
          {:else}
            Day {day} · {weekdayNameOfProjectDay(day)}
          {/if}
          {#if isToday}<span class="today-now">Today</span>{/if}
          {#if isProjectStart}<span class="today-start">Project start</span>{/if}
          {#if isWeekendDay(day) && !$allowWeekendWork}<span class="today-off">Non-working day</span>{/if}
        </div>
        <div class="today-sub">
          Week {weekOfProjectDay(day)} · Day {day}{calendarLabel ? ` · ${weekdayNameOfProjectDay(day)}` : ""}
        </div>
      </div>

      <div class="today-controls">
        {#if clockDay != null && !isToday}
          <button class="today-jump" type="button" on:click={backToToday}>Jump to today</button>
        {/if}
        <button class="today-jump" type="button" on:click={toggleFullscreen}>
          {fullscreen ? "Exit full screen" : "Full screen"}
        </button>
        <button class="today-nav" type="button" title="Previous day" disabled={day <= 0} on:click={() => step(-1)}>‹</button>
        <button class="today-nav" type="button" title="Next day" disabled={day >= lastDay} on:click={() => step(1)}>›</button>
      </div>
    </header>

    <div class="today-tally">
      <span class="tally open">{openCount} to do</span>
      <span class="tally done">{doneCount} done</span>
      {#if blockedCount}<span class="tally blocked">{blockedCount} blocked</span>{/if}
      <span class="tally quiet">{startingCount} starting today</span>
    </div>

    <section class="today-site">
      <h3 class="today-site-head">Site status</h3>
      <div class="today-site-grid">
        {#each $statusRows as row (row.id)}
          {@const explicit = row.values[day] ?? null}
          {@const dependents = tasksOnDayRequiringRow(dayCards, day, row.id)}
          {@const required = requiredRowValue(dependents, row.id)}
          {@const suggested = explicit == null && required != null}
          {@const v = explicit ?? required}
          {@const tone = statusTone(row, v)}
          <button
            class="site-chip"
            class:good={tone === "good"}
            class:attention={tone === "attention"}
            class:unset={tone === "unset"}
            class:suggested={suggested}
            class:readonly={!editable}
            type="button"
            disabled={!editable}
            title={editable ? `Click to cycle: not set → ${row.onLabel} → ${row.offLabel}` : undefined}
            on:click={() => cycleStatusDay(row.id, day, required)}
          >
            <span class="site-chip-label">{row.label}</span>
            <span class="site-chip-value">{v == null ? "Not set" : statusLabel(row, v)}</span>
          </button>
        {/each}
      </div>
      <div class="today-resources">
        <div class="resource">
          <span class="resource-label">🔧 Equipment</span>
          <span class="resource-value">{resources.equipment.length ? resources.equipment.join(" · ") : "None needed"}</span>
        </div>
        <div class="resource">
          <span class="resource-label">👷 Personnel</span>
          <span class="resource-value">{resources.personnel.length ? resources.personnel.join(" · ") : "None needed"}</span>
        </div>
      </div>
    </section>

    {#each shiftBuckets as bucket (bucket.shift ?? "__all__")}
      {#if bucket.shift}
        <h3 class="today-shift-head">{SHIFT_LABEL[bucket.shift]}</h3>
      {/if}
      {#if !bucket.groups.length}
        <div class="today-empty">
          {#if isWeekendDay(day) && !$allowWeekendWork}
            Nothing scheduled — weekends are non-working days.
          {:else}
            Nothing scheduled {bucket.shift ? "this shift" : "on this day"}.
          {/if}
        </div>
      {:else}
      {#each bucket.groups as group (group.phase ?? "__all__")}
        <section class="today-phase">
          <h3 class="today-phase-head">
            {#if group.phase}
              <span class="today-phase-dot" style="background:{phaseColor($projectPhases, group.phase)}"></span>
              {group.phase}
            {:else}
              All phases
            {/if}
            <span class="today-phase-count">{group.cards.length}</span>
          </h3>
          <div class="today-cards">
            {#each group.cards as card (card.task.id)}
              {@const t = card.task}
              {@const preds = card.index === 1 ? predecessorsOf(t) : []}
              {@const conflicts = statusConflicts(card, $statusRows)}
              <article class="tcard" class:done={card.done} class:blocked={t.blocked && !card.done} class:critical={card.critical && !card.done}>
                <div class="tcard-top">
                  <button class="tcard-id" type="button" title="Edit {t.name}" on:click={() => openEditModal(t.id)}>{wbsLabel(t, $wbsNodes)}</button>
                  {#if t.type === "milestone"}
                    <span class="tcard-flag milestone">Milestone</span>
                  {:else if card.critical && !card.done}
                    <span class="tcard-flag crit">Critical</span>
                  {/if}
                  {#if card.span > 1}
                    <span class="tcard-progress">Day {card.index} of {card.span}</span>
                  {/if}
                </div>

                <h4 class="tcard-name">{t.name}</h4>

                {#if t.responsibleParty || t.subcontractors.length || (conflicts.length && !card.done)}
                  <div class="tcard-who">
                    {#if t.responsibleParty}<span class="who party">{t.responsibleParty}</span>{/if}
                    {#each t.subcontractors as sc}<span class="who subc" style="background:{subcontractorColor($subcontractorColors, sc)}">{sc}</span>{/each}
                    {#if conflicts.length && !card.done}
                      {#each conflicts as c (c.row.id)}
                        <span class="who status-req" title="{c.row.label} must be {c.wantLabel}">⚡ {c.row.label}: {c.wantLabel}</span>
                      {/each}
                    {/if}
                  </div>
                {/if}

                {#if preds.length}
                  <div class="tcard-wait">
                    <span class="wait-label">Waiting on</span>
                    {#each preds as p (p.id)}
                      <span class="wait-item" class:ok={p.complete} title={p.name}>
                        {p.complete ? "✓" : "•"} {p.wbs} — {p.name}
                      </span>
                    {/each}
                  </div>
                {/if}

                {#if t.blocked}
                  <div class="tcard-block">
                    {#if editingReasonFor === t.id}
                      <input
                        class="block-input"
                        type="text"
                        bind:value={reasonDraft}
                        placeholder="What's blocking it?"
                        on:keydown={(e) => onReasonKey(e, t, saveReason)}
                        on:blur={() => saveReason(t)}
                      />
                    {:else}
                      <button class="block-reason" type="button" disabled={!editable} on:click={() => editReason(t)}>
                        ⚑ {t.blockedReason || "Blocked — add a reason"}
                      </button>
                    {/if}
                  </div>
                {:else if editingReasonFor === t.id}
                  <div class="tcard-block">
                    <input
                      class="block-input"
                      type="text"
                      bind:value={reasonDraft}
                      placeholder="What's blocking it?"
                      on:keydown={(e) => onReasonKey(e, t, confirmBlock)}
                      on:blur={() => confirmBlock(t)}
                    />
                  </div>
                {/if}

                {#if editable}
                  <div class="tcard-actions">
                    <button
                      class="tbtn done-btn"
                      class:active={card.done}
                      type="button"
                      on:click={() => toggleTaskDayDone(t.id, day)}
                    >
                      {card.done ? "✓ Done" : "Mark done"}
                    </button>
                    <button class="tbtn block-btn" class:active={t.blocked} type="button" on:click={() => startBlocking(t)}>
                      {t.blocked ? "Unblock" : "⚑ Blocked"}
                    </button>
                  </div>
                {/if}
              </article>
            {/each}
          </div>
        </section>
      {/each}
      {/if}
    {/each}
  {/if}
</div>
